import React, { Component } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    Select,
    MenuItem,
    FormControl,
    CircularProgress
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import EqualizerIcon from '@mui/icons-material/Equalizer';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { apiUrl } from '../../comp/ApiUrl';
import ReportDetails from './ReportDetails';

// Dicionário amigável de tradução para enviar os parâmetros corretos à API
const PERIODOS_MAP = {
    'Hoje': 'hoje',
    'Ontem': 'ontem',
    'Últimos 7 dias': 'ultimos_7',
    'Últimos 14 dias': 'ultimos_14',
    'Últimos 30 dias': 'ultimos_30',
    'Últimos 60 dias': 'ultimos_60',
    'Últimos 90 dias': 'ultimos_90',
    'Este mês': 'este_mes',
    'Mês anterior': 'mes_anterior',
    'Este ano': 'este_ano',
    'Todo o período': 'todo_periodo'
};

class Reports extends Component {
    constructor(props) {
        super(props);
        this.state = {
            periodo: 'Hoje', // Inicializado com a string correspondente às chaves do mapa
            dados: null,
            loading: true,
            error: null
        };
    }

    componentDidMount() {
        this.fetchDados();
    }

    // DISPARA UM NOVO FETCH AUTOMATICAMENTE SE O USUÁRIO ALTERAR O PERÍODO NO SELECT
    componentDidUpdate(prevProps, prevState) {
        if (prevState.periodo !== this.state.periodo) {
            this.fetchDados();
        }
    }

    fetchDados = () => {
        this.setState({ loading: true, error: null });

        // Extrai a string técnica mapeada
        const queryPeriodo = PERIODOS_MAP[this.state.periodo] || 'ultimos_30';

        fetch(`${apiUrl}/store/reports?periodo=${queryPeriodo}`, {
            credentials: 'include',
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Falha ao buscar dados do servidor');
                }
                return response.json();
            })
            .then(data => {
                this.setState({ dados: data, loading: false });
            })
            .catch(err => {
                console.error("Erro na requisição:", err);
                this.setState({ error: err.message, loading: false });
            });
    };

    handleChangePeriodo = (event) => {
        this.setState({ periodo: event.target.value });
    };

    formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
    };

    render() {
        const { periodo, dados, loading, error } = this.state;

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
                    <CircularProgress sx={{ color: '#7b2cbf' }} />
                </Box>
            );
        }

        if (error) {
            return (
                <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
                    <Typography color="error" variant="h6">Ocorreu um erro ao carregar o relatório.</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{error}</Typography>
                    <Button variant="contained" onClick={this.fetchDados} sx={{ backgroundColor: '#7b2cbf', '&:hover': { backgroundColor: '#62229b' } }}> Tentar Novamente </Button>
                </Box>
            );
        }

        return (
            <Box sx={{ p: 0, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>

                <Card sx={{
                    borderRadius: 3,
                    mb: 3,
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #f0f0f0'
                }}>
                    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>

                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                            flexWrap: 'wrap',
                            gap: 2
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    backgroundColor: 'rgba(123, 44, 191, 0.08)',
                                    p: 1,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#7b2cbf'
                                }}>
                                    <EqualizerIcon fontSize="small" />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
                                        Análise de Vendas
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.2 }}>
                                        Dados consolidados do período
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<FileDownloadOutlinedIcon />}
                                    endIcon={<KeyboardArrowDownIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        color: '#4a4a4a',
                                        borderColor: '#e0e0e0',
                                        borderRadius: 2.5,
                                        px: 2,
                                        fontWeight: 500,
                                        '&:hover': { borderColor: '#7b2cbf', color: '#7b2cbf', backgroundColor: 'rgba(123, 44, 191, 0.04)' }
                                    }}
                                >
                                    Exportar
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={this.state.loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                                    onClick={this.fetchDados}
                                    disabled={this.state.loading}
                                    sx={{
                                        textTransform: 'none',
                                        color: '#4a4a4a',
                                        borderColor: '#e0e0e0',
                                        borderRadius: 2.5,
                                        px: 2,
                                        fontWeight: 500,
                                        '&:hover': { borderColor: '#b1006a', color: '#b1006a', backgroundColor: 'rgba(177, 0, 106, 0.04)' }
                                    }}
                                >
                                    {this.state.loading ? 'Atualizando...' : 'Atualizar'}
                                </Button>
                            </Box>
                        </Box>

                        {/* Dropdown alimentado com a lista completa obtida da sua imagem */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: 1.5,
                            maxWidth: { sm: '450px', xs: '100%' }
                        }}>
                            <FormControl size="small" sx={{ flexGrow: 1 }}>
                                <Select
                                    value={periodo}
                                    onChange={this.handleChangePeriodo}
                                    IconComponent={KeyboardArrowDownIcon}
                                    startAdornment={<CalendarTodayIcon sx={{ mr: 1, color: '#7b2cbf', fontSize: 18 }} />}
                                    sx={{
                                        borderRadius: 2.5,
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7b2cbf' },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7b2cbf' }
                                    }}
                                >
                                    {Object.keys(PERIODOS_MAP).map((label) => (
                                        <MenuItem key={label} value={label}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Badge adaptativo com as strings calculadas no backend */}
                            <Box sx={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                px: 2,
                                borderRadius: 2.5,
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                color: '#495057',
                                display: 'flex',
                                alignItems: 'center',
                                whiteSpace: 'nowrap'
                            }}>
                                {dados?.dataInicioFormatada && dados?.dataFimFormatada
                                    ? `${dados.dataInicioFormatada} - ${dados.dataFimFormatada}`
                                    : '--/--'
                                }
                            </Box>
                        </Box>
                    </CardContent>
                </Card>





                <ReportDetails data={dados} />
            </Box>
        );
    }
}

export default Reports;